import React, { useState, useEffect, useRef, useContext } from "react";
import supabase from "../supabaseClient";
import { Autocomplete, TextField, Paper, Avatar, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import UserContext from "../contexts/userContext";

function DynamicFilter({ selectedFilters = [], updateFilters, filterOptions }) {
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accentPrimary = theme.palette.accent?.primary || theme.palette.primary.main;

  let defaultFilterOptions = ["Book", "Author", "Time", "Tag"];
  if (user) {
    defaultFilterOptions.push("Owner");
  }
  const filterTypes = filterOptions || defaultFilterOptions;

  const [items, setItems] = useState([]);
  const [autocompleteValue, setAutocompleteValue] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [filterType, setFilterType] = useState(filterTypes[0]);

  const inputRef = useRef(null);

  useEffect(() => {
    const fetchItems = async () => {
      if (filterType === "Time") {
        setItems([]);
      } else if (filterType === "Owner") {
        if (user) {
          setItems([user]);
        } else {
          setItems([]);
        }
      } else if (filterType === "Tag") {
        const { data, error } = await supabase
          .from("tags")
          .select("name, category:tags_categories(name)")
          .eq("is_public", true);

        if (error) {
          console.error("Error fetching tags:", error);
        } else {
          setItems(data);
        }
      } else {
        const { data, error } = await supabase
          .from("books")
          .select(filterType === "Book" ? "name" : "author");
        if (error) {
          console.error(`Error fetching ${filterType.toLowerCase()}s:`, error);
        } else {
          const mappedItems =
            filterType === "Book"
              ? [...new Set(data.map((item) => item.name))]
              : [
                  ...new Set(
                    data.filter((item) => item.author !== null).map((item) => item.author),
                  ),
                ];
          setItems(mappedItems);
        }
      }
    };

    fetchItems();
  }, [filterType, user]);

  const filteredItems = items.filter((item) => {
    if (filterType === "Owner") {
      return !selectedFilters.some(
        (filter) => filter.type === filterType && filter.value === item.id,
      );
    } else if (filterType === "Tag") {
      return !selectedFilters.some(
        (filter) => filter.type === filterType && filter.value === item.name,
      );
    }
    return !selectedFilters.some(
      (filter) => filter.type === filterType && filter.value === item,
    );
  });

  const filterLabels = {
    Book: "source",
    Author: "author",
    Owner: "owner",
    Time: "active time (minutes)",
    Tag: "tag",
  };

  // Shared input styles
  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      fontFamily: theme.typography.fontFamily,
      fontSize: '0.85rem',
      borderRadius: '12px',
      backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      '& fieldset': {
        borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        borderWidth: '1px',
        transition: 'border-color 0.2s ease',
      },
      '&:hover fieldset': {
        borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
      },
      '&.Mui-focused fieldset': {
        borderColor: accentPrimary,
        borderWidth: '1px',
      },
    },
    "& .MuiInputLabel-outlined": {
      fontFamily: theme.typography.fontFamily,
      fontSize: '0.85rem',
      color: theme.palette.text.secondary,
      '&.Mui-focused': {
        color: accentPrimary,
      },
    },
  };

  // Select dropdown styles
  const selectStyles = {
    border: "none",
    background: isDark 
      ? `${accentPrimary}15`
      : `${accentPrimary}10`,
    color: accentPrimary,
    marginRight: theme.spacing(1.5),
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '4px 8px',
    borderRadius: '6px',
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.15s ease',
  };

  // Glass paper styles for dropdown
  const glassPaperStyles = {
    background: isDark
      ? "linear-gradient(135deg, rgba(30, 30, 35, 0.95) 0%, rgba(20, 20, 25, 0.98) 100%)"
      : "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.99) 100%)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
    borderRadius: '12px',
    fontFamily: theme.typography.fontFamily,
    fontSize: "0.85rem",
    maxHeight: 300,
    overflow: "auto",
    boxShadow: isDark
      ? '0 8px 32px -8px rgba(0,0,0,0.5)'
      : '0 8px 32px -8px rgba(0,0,0,0.15)',
    mt: 0.5,
    "& .MuiAutocomplete-option": {
      fontFamily: theme.typography.fontFamily,
      fontSize: '0.85rem',
      padding: '10px 14px',
      borderRadius: '8px',
      margin: '2px 4px',
      transition: 'background-color 0.15s ease',
      '&:hover': {
        backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
      },
      '&[aria-selected="true"]': {
        backgroundColor: isDark ? `${accentPrimary}20` : `${accentPrimary}12`,
        color: accentPrimary,
      },
    },
    "& .MuiAutocomplete-groupLabel": {
      fontFamily: theme.typography.fontFamily,
      fontSize: '0.7rem',
      fontWeight: 600,
      color: theme.palette.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      padding: '8px 14px 4px',
      backgroundColor: 'transparent',
    },
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
      {filterType === "Time" ? (
        <TextField
          sx={{
            width: "60%",
            ...inputStyles,
          }}
          label={filterLabels[filterType]}
          variant="outlined"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && inputValue && !isNaN(inputValue)) {
              updateFilters("Time", inputValue);
              setInputValue("");
              e.target.blur();
            }
          }}
          InputProps={{
            startAdornment: (
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                style={selectStyles}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                {filterTypes.map(
                  (type) =>
                    !(type === "Owner" && !user) && (
                      <option key={type} value={type}>
                        {type.toLowerCase()}
                      </option>
                    ),
                )}
              </select>
            ),
          }}
        />
      ) : (
        <Autocomplete
          sx={{ width: "60%" }}
          options={filteredItems}
          groupBy={
            filterType === "Tag"
              ? (option) => (option.category && option.category.name) || "Uncategorized"
              : undefined
          }
          getOptionLabel={(option) => {
            if (filterType === "Owner") {
              return option.email;
            } else if (filterType === "Tag") {
              return option.name;
            }
            return option;
          }}
          value={autocompleteValue}
          inputValue={inputValue}
          onChange={(event, newValue) => {
            if (filterType === "Owner") {
              updateFilters(filterType, newValue.id);
            } else if (filterType === "Tag") {
              updateFilters(filterType, newValue.name);
            } else {
              updateFilters(filterType, newValue);
            }
            setAutocompleteValue(null);
            setInputValue("");

            if (inputRef.current) {
              inputRef.current.blur();
            }
          }}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          renderOption={(props, option) => {
            if (filterType === "Owner") {
              return (
                <li {...props}>
                  <Avatar
                    src={option?.user_metadata?.avatar_url}
                    alt={option.email}
                    sx={{
                      width: 28,
                      height: 28,
                      marginRight: theme.spacing(1.5),
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                    }}
                  />
                  <span style={{ fontWeight: 500 }}>{option.email}</span>
                </li>
              );
            } else if (filterType === "Tag") {
              return (
                <li {...props}>
                  <span>{option.name}</span>
                </li>
              );
            }
            return <li {...props}>{option}</li>;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={filterLabels[filterType] || ""}
              variant="outlined"
              fullWidth
              inputRef={inputRef}
              sx={inputStyles}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      style={selectStyles}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {filterTypes.map(
                        (type) =>
                          !(type === "Owner" && !user) && (
                            <option key={type} value={type}>
                              {type.toLowerCase()}
                            </option>
                          ),
                      )}
                    </select>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          PaperComponent={({ children }) => (
            <Paper
              sx={glassPaperStyles}
              elevation={0}
            >
              {children}
            </Paper>
          )}
        />
      )}
    </Box>
  );
}

export default DynamicFilter;
